import { useEffect, useState, Suspense, lazy } from 'react';
import { AppStateProvider } from './context/AppStateContext';
import {
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { FullScreenHandler } from './components/layout/FullScreenHandler';
import { ProtectedRoute } from './components/routes/ProtectedRoute';
import LoginPage from './components/pages/Login';
import PWAPrompt from './components/PWAPrompt';
import ServiceWorkerUpdatePrompt from './components/ServiceWorkerUpdatePrompt';
import { useServiceWorker } from './hooks/useServiceWorker';
// import { useMobileSetup } from './hooks/useMobileSetup';
import { MoldProvider } from './context/MoldContext';
import { MoldOrderProvider } from './context/MoldOrderContext';
import { setAuthToken } from './services/api';
import { ROUTES } from './constants/routes';
import { FontSizeProvider } from './context/FontSizeContext';
import { PreferencesProvider } from './context/PreferencesContext';
import FontSizeControls from './components/FontSizeControls';
import MainContainer from './components/layout/MainContainer';
import { AdminRoute } from './components/routes/AdminRoute';
import CornerLabel from './components/layout/CornerLabel';
import ImpersonationBanner from './components/layout/ImpersonationBanner';
import { BundleDebugInfo } from './components/debug/BundleDebugInfo';
import { useAuth } from './hooks/useAuth';
import { resolveClientCompanyCode } from './utils/company';
import { isHeadlessPrintRoute } from './utils/printRoute';

// Direct lazy imports at route level
const HomeContainer = lazy(() => import('./containers/HomeContainer'));
const MoldListContainer = lazy(() => import('./containers/MoldListContainer'));
const MoldDetailContainer = lazy(() => import('./containers/MoldDetailContainer'));
const KanbanBoardContainer = lazy(() => import('./containers/KanbanBoardContainer'));
const TVBoardContainer = lazy(() => import('./containers/TVBoardContainer'));
const ReportContainer = lazy(() => import('./containers/ReportContainer'));
const TechnicianOrdersContainer = lazy(() => import('./containers/TechnicianOrdersContainer'));
const CreateMoldOrderContainer = lazy(() => import('./containers/CreateMoldOrderContainer'));
const CreateMoldOrderMenuContainer = lazy(() => import('./containers/CreateMoldOrderMenuContainer'));
const CreateMoldContainer = lazy(() => import('./containers/CreateNewMoldContainer'));
const MoldOrderDetailContainer = lazy(() => import('./containers/MoldOrderDetailContainer'));
const QuestionnaireContainer = lazy(() => import('./containers/QuestionnaireContainer'));
const UserListContainer = lazy(() => import('./containers/UserListContainer'));
const MoldOrderListContainer = lazy(() => import('./containers/MoldOrderListContainer'));
const MoldOrderListV2Container = lazy(() => import('./containers/MoldOrderListV2Container'));
const MoldOrderPrioritizerContainer = lazy(() => import('./containers/MoldOrderPrioritizerContainer'));
const ProductPartListContainer = lazy(() => import('./containers/ProductPartListContainer'));
const InsertListContainer = lazy(() => import('./containers/InsertListContainer'));
const QRCodeScanner = lazy(() => import('./components/QRCodeScanner'));
const Configuration = lazy(() => import('./components/pages/Configuration'));
const CustomerList = lazy(() => import('./components/CustomerList'));
const ShotCountLogPage = lazy(() => import('./components/ShotCountUpdateLog/ShotCountLogPage'));
const FormJsonEditorPage = lazy(() => import('./components/pages/FormJsonEditorPage'));
const OrderExportPage = lazy(() => import('./components/pages/OrderExportPage'));
const MoldOrderTypeExplorer = lazy(() => import('./components/pages/MoldOrderTypeExplorer'));
const SystemStatusPage = lazy(() => import('./components/pages/SystemStatusPage'));
const WorkflowVisualizer = lazy(() => import('./components/pages/WorkflowVisualizer'));
const NotificationsPage = lazy(() => import('./components/pages/NotificationsPage'));
const MyProfilePage = lazy(() => import('./components/pages/MyProfilePage'));
const MyWorksContainer = lazy(() => import('./containers/MyWorksContainer'));
const PrintMoldOrderReport = lazy(() => import('./components/pages/PrintMoldOrderReport'));
const StateActivityPage = lazy(() => import('./components/pages/StateActivityPage'));
const PrintFormPreview = lazy(() => import('./components/pages/PrintFormPreview'));

function App() {
  const location = useLocation();
  const { login } = useAuth();
  const companyCode = resolveClientCompanyCode();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const stage = (process.env.VITE_STAGE || '').toLowerCase();
  const isDevStage = ['dev', 'development'].includes(stage);
  const isDevExplorerBypassRoute = location.pathname === ROUTES.MOLD_ORDER_TYPE_EXPLORER;
  const devBypassUsername = process.env.CYPRESS_USERNAME || 'admin';
  const devBypassPassword = process.env.CYPRESS_PASSWORD || 'password';

  // Standalone PDF print route: self-authorizes from a ?token= query param and
  // mounts its own provider stack. It must bypass the auth-shell skeleton and
  // ProtectedRoute entirely so the headless renderer never captures a login or
  // loading screen. Detected here so it renders before initializeAuth gating.
  const isPrintRoute = isHeadlessPrintRoute(location.pathname);

  const { needRefresh, updateServiceWorker } = useServiceWorker();

  const handleLogin = (token: string) => {
    setAuthToken(token);
    setIsAuthenticated(true);
  };

  const initializeAuth = async () => {
    try {
      // An active impersonation session (per-tab, sessionStorage-only) wins over
      // the admin's real token so a refresh mid-impersonation stays impersonated.
      const token =
        sessionStorage.getItem('impersonation_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('token');

      if (token) {
        setAuthToken(token);
        setIsAuthenticated(true);
        return;
      }

      // Allow direct access to the workflow explorer in local dev without a manual login.
      if (isDevStage && isDevExplorerBypassRoute) {
        const bypassToken = await login(devBypassUsername, devBypassPassword);
        setAuthToken(bypassToken);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      // Clear potentially corrupted tokens
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
    } finally {
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    // The /print/ route self-authorizes from its ?token= URL param via the print
    // hook (axios header only, no storage). Skip app boot auth there so a stale
    // token in the long-lived headless Chrome profile's localStorage can never
    // overwrite the fresh URL token, and so we never flip app auth state / redirect
    // on the headless surface.
    if (isPrintRoute) return;
    initializeAuth();
  }, [devBypassPassword, devBypassUsername, isDevExplorerBypassRoute, isDevStage, isPrintRoute, login]);

  if (isPrintRoute) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route
            path="/print/mold-orders/:id"
            element={<PrintMoldOrderReport />}
          />
          <Route
            path="/print/form-preview/:previewId"
            element={<PrintFormPreview />}
          />
        </Routes>
      </Suspense>
    );
  }

  if (!isInitialized) {
    return (
      <AppStateProvider companyCode={companyCode}>
        <MainContainer>
          <div className="h-full flex items-center justify-center">
            <div className="animate-pulse space-y-4 w-full max-w-3xl px-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded-lg" />
              ))}
            </div>
          </div>
        </MainContainer>
      </AppStateProvider>
    );
  }

  return (
    <AppStateProvider companyCode={companyCode}>
      <PreferencesProvider>
        <FontSizeProvider>
          <FullScreenHandler>
            <MoldOrderProvider>
              <MoldProvider>
                <CornerLabel />
                <ImpersonationBanner />
                <ServiceWorkerUpdatePrompt
                  show={needRefresh}
                  onReload={updateServiceWorker}
                />
                <BundleDebugInfo enabled={false && process.env.NODE_ENV === 'development'} />
                {/* !!! Change font of entire system here -- set font family in tailwind.config.js !!!*/}
                <div className="font-noto-serif-thai relative h-dvh w-screen">
                  <Routes>
                    <Route
                      path="/login"
                      element={
                        isAuthenticated ? (
                          <Navigate to="/" replace />
                        ) : (
                          <LoginPage onLoginSuccess={handleLogin} />
                        )
                      }
                    />
                    {/* Protected routes */}
                    <Route
                      path="/*"
                      element={
                        <ProtectedRoute isAuthenticated={isAuthenticated}>
                          <Suspense fallback={
                            <div className="flex items-center justify-center h-screen bg-gray-50">
                              <div className="animate-pulse space-y-4 w-full max-w-md px-4">
                                <div className="h-12 bg-gray-200 rounded-lg w-3/4 mx-auto"></div>
                                <div className="space-y-3">
                                  <div className="h-8 bg-gray-200 rounded"></div>
                                  <div className="h-8 bg-gray-200 rounded w-5/6"></div>
                                  <div className="h-8 bg-gray-200 rounded w-4/6"></div>
                                </div>
                              </div>
                            </div>
                          }>
                            <Routes>
                            <Route path="/" element={<HomeContainer />} />
                            <Route
                              path={ROUTES.MOLDS}
                              element={<MoldListContainer />}
                            />
                            <Route
                              path={ROUTES.MOLD_DETAILS}
                              element={<MoldDetailContainer />}
                            />
                            <Route
                              path={ROUTES.DASHBOARD}
                              element={<KanbanBoardContainer />}
                            />
                            <Route
                              path={ROUTES.TVDASHBOARD}
                              element={<TVBoardContainer />}
                            />
                            <Route
                              path={ROUTES.REPORT}
                              element={<ReportContainer />}
                            />
                            <Route
                              path={ROUTES.STATE_ACTIVITIES}
                              element={<StateActivityPage />}
                            />
                            <Route
                              path={ROUTES.TECHNICIAN_ORDERS}
                              element={<TechnicianOrdersContainer />}
                            />
                            <Route
                              path={ROUTES.CREATE_MOLD_ORDERS}
                              element={<CreateMoldOrderContainer />}
                            />
                            <Route
                              path={ROUTES.CREATE_MOLD_ORDERS_MENU}
                              element={<CreateMoldOrderMenuContainer />}
                            />
                            <Route
                              path={ROUTES.CREATE_NEW_MOLD}
                              element={<CreateMoldContainer />}
                            />
                            {/* v1 backup ('/mold-orders/v1') and the legacy '/mold-orders/v2'
                                redirect must be declared BEFORE MOLD_ORDER_DETAIL
                                ('/mold-orders/:id') so "v1"/"v2" are never captured as an
                                order id. v2 is now the default list at '/mold-orders'. */}
                            <Route
                              path={ROUTES.MOLD_ORDERS_V1}
                              element={<MoldOrderListContainer />}
                            />
                            <Route
                              path="/mold-orders/v2"
                              element={<Navigate to={ROUTES.MOLD_ORDERS} replace />}
                            />
                            <Route
                              path={ROUTES.MOLD_ORDER_DETAIL}
                              element={<MoldOrderDetailContainer />}
                            />
                            <Route
                              path={ROUTES.NOTIFICATIONS}
                              element={
                                <MainContainer>
                                  <NotificationsPage />
                                </MainContainer>
                              }
                            />
                            <Route
                              path={ROUTES.MY_PROFILE}
                              element={
                                <MainContainer title="โปรไฟล์ของฉัน">
                                  <MyProfilePage />
                                </MainContainer>
                              }
                            />
                            <Route
                              path={ROUTES.MY_WORKS}
                              element={<MyWorksContainer />}
                            />
                            <Route
                              path={ROUTES.QUESTIONNAIRE}
                              element={<QuestionnaireContainer />}
                            />
                            <Route
                              path={ROUTES.CUSTOMERS}
                              element={
                                <MainContainer>
                                  <CustomerList />
                                </MainContainer>
                              }
                            />
                            <Route
                              path={ROUTES.MOLD_ORDERS}
                              element={<MoldOrderListV2Container />}
                            />
                            <Route
                              path={ROUTES.USERS}
                              element={
                                <AdminRoute>
                                  <UserListContainer />
                                </AdminRoute>
                              }
                            />
                            <Route
                              path={ROUTES.PRODUCT_PARTS}
                              element={<ProductPartListContainer />}
                            />
                            <Route
                              path={ROUTES.INSERTS}
                              element={<InsertListContainer />}
                            />
                            <Route path="/scan" element={<QRCodeScanner />} />
                            <Route
                              path={ROUTES.SORT_MOLD_ORDERS}
                              element={<MoldOrderPrioritizerContainer />} />
                            <Route
                              path={ROUTES.CONFIGURATION}
                              element={
                                <AdminRoute>
                                  <Configuration />
                                </AdminRoute>
                              }
                            />
                            <Route
                              path={ROUTES.SHOTCOUNT_LOG}
                              element={<ShotCountLogPage />}
                            />
                            <Route
                              path={ROUTES.FORM_JSON_EDITOR}
                              element={
                                <AdminRoute>
                                  <FormJsonEditorPage />
                                </AdminRoute>
                              }
                            />
                            <Route
                              path={ROUTES.ORDER_EXPORT}
                              element={
                                <AdminRoute>
                                  <MainContainer>
                                    <OrderExportPage />
                                  </MainContainer>
                                </AdminRoute>
                              }
                            />
                            <Route
                              path={ROUTES.MOLD_ORDER_TYPE_EXPLORER}
                              element={
                                <AdminRoute>
                                  <MainContainer>
                                    <MoldOrderTypeExplorer />
                                  </MainContainer>
                                </AdminRoute>
                              }
                            />
                            <Route
                              path={ROUTES.SYSTEM_STATUS}
                              element={
                                <AdminRoute>
                                  <MainContainer>
                                    <SystemStatusPage />
                                  </MainContainer>
                                </AdminRoute>
                              }
                            />
                            <Route
                              path={ROUTES.WORKFLOWS}
                              element={
                                <MainContainer>
                                  <WorkflowVisualizer />
                                </MainContainer>
                              }
                            />
                          </Routes>
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                  <PWAPrompt />
                  {isAuthenticated && <FontSizeControls />}
                </div>
              </MoldProvider>
            </MoldOrderProvider>
          </FullScreenHandler>
        </FontSizeProvider>
      </PreferencesProvider>
    </AppStateProvider>
  );
}

export default App;
